/**
 * SafeCurves layer assembly: schema version, the module icon list, and
 * `buildSystemLayers`, which walks the `moduleTable` (see `layerConfig.ts`)
 * to build the `ISafeCurvesSystemLayers` envelope from the flat
 * operational-feature list.
 *
 * Per-layer config (style JSON carrying type/zoom/layout/paint, z-order,
 * query properties) lives on the table entries, not here, so this file never
 * changes when a layer is added.
 */

import { getLogger } from '@logtape/logtape';
import { buildModuleLayers } from '#lib/common/moduleTable.js';
import type { ModuleDefinition } from '#lib/apps/types.js';
import IconModuleIdentify from '#lib/assets/modules/identify.svg?raw';
import IconModulePrioritize from '#lib/assets/modules/prioritize.svg?raw';
import IconModuleInterventions from '#lib/assets/modules/interventions.svg?raw';
import { EProduct, ESafeCurvesModule, type ISafeCurvesFeature } from './interfaces';
import { moduleTable, type ISafeCurvesSystemLayers } from './layerConfig';
import type { ILayerSource, TLayer } from '#lib/types.js';
import type { LayerEntry } from '#lib/common/moduleTable.js';
import { getVectorLayerSource, groupByModule } from '#lib/common/layers.js';

// GeoServer dataset this app owns; drives availability and layer filtering.
export const SC_DATASET = 'safecurves';

const log = getLogger(['safesystem', 'safecurves', 'layers']);

/**
 * Optional grouping axis. SafeCurves groups its operational layers into
 * Identify, Prioritize, and Interventions. Apps without modules omit this
 * field entirely.
 */
export const modules: ModuleDefinition[] = [
	{ id: ESafeCurvesModule.Identify, label: 'Identify', icon: IconModuleIdentify },
	{ id: ESafeCurvesModule.Prioritize, label: 'Prioritize', icon: IconModulePrioritize },
	{ id: ESafeCurvesModule.Interventions, label: 'Interventions', icon: IconModuleInterventions }
];

// Source id a local layer's GeoJSON is pushed into (see `map.geojsonSources`); layers sharing a `featureTitle` share it.
export const localSourceId = (layerId: string) => `${layerId}-source`;

// An empty GeoJSON source the app fills once the map has loaded; `promoteId` gives the table and highlight stable ids.
function localLayerSource(layerId: string, def: LayerEntry, abstract: string): ILayerSource {
	return {
		id: layerId,
		zIndex: def.map.zIndex,
		title: layerId,
		abstract,
		selectable: def.map.selectable,
		source: {
			type: 'geojson',
			data: { type: 'FeatureCollection', features: [] },
			promoteId: 'id'
		} as ILayerSource['source'],
		layer: {
			...(def.map.style as Partial<TLayer>),
			id: layerId,
			source: localSourceId(def.featureTitle ?? layerId)
		}
	};
}

/**
 * Build the nested `ISafeCurvesSystemLayers` from a flat list of operational
 * features. `appLayers` is expected to already be filtered to the app's active
 * version (see `loadSystem`); this keeps only the SafeCurves dataset and groups
 * it by module.
 *
 * Returns null when SafeCurves product data is missing; the caller treats
 * this as a system-data load failure.
 */
export function buildSystemLayers(appLayers: ISafeCurvesFeature[]): ISafeCurvesSystemLayers | null {
	const scLayers = groupByModule(appLayers, EProduct.SafeCurves, SC_DATASET);
	if (!scLayers) return null;

	// The style JSON is a MapLibre layer fragment; JSON imports widen `type`, so narrow here.
	return buildModuleLayers(
		moduleTable,
		scLayers,
		(layerId, feature, def) =>
			def.local
				? localLayerSource(layerId, def, def.local.abstract)
				: getVectorLayerSource(layerId, feature, def.map.style as Partial<TLayer>, def.map.zIndex, {
						selectable: def.map.selectable
					}),
		log
	) as ISafeCurvesSystemLayers;
}
